import DataTable from 'react-data-table-component';
import { useState, useEffect } from 'react';
import "./index.css";

const GuestTable = ({ guests }) => {
    const guestColumns = [
        {
            name: "Name",
            selector: (row) => `${row.first_name} ${row.last_name}`,
            minWidth: "150px"
        },
        {
            name: "Lead Guest",
            selector: (row) => `${row.isleadPax}`,
            cell: row => <span className="capitalize">
                {row.isleadPax ? "True" : "False"}
            </span>,
            minWidth: "150px"
        },
        {
            name: "Passport No.",
            selector: (row) => row.passport_number || "N/A",
            cell: row => <span className={!row.passport_number ? 'text-muted' : ''}>
                {row.passport_number || "N/A"}
            </span>,
            minWidth: "120px"
        },
        {
            name: "Contact Email",
            selector: (row) => row.contact_email || "N/A",
            cell: row => <span className={!row.contact_email ? 'text-muted' : ''}>
                {row.contact_email || "N/A"}
            </span>,
            minWidth: "180px"
        },
        {
            name: "Contact Phone",
            selector: (row) => row.contact_phone || "N/A",
            cell: row => <span className={!row.contact_phone ? 'text-muted' : ''}>
                {row.contact_phone || "N/A"}
            </span>,
            minWidth: "120px"
        },
        {
            name: "Country",
            selector: (row) => row.country_of_residence || "N/A",
            cell: row => <span className={!row.country_of_residence ? 'text-muted' : ''}>
                {row.country_of_residence || "N/A"}
            </span>,
            minWidth: "120px"
        },
        {
            name: "Guest ID",
            selector: (row) => row.guest_id || "N/A",
            cell: row => <span>
                {row.guest_id || "N/A"}
            </span>,
            minWidth: "150px"
        },
    ];

    return (
        <div className="guest-table-container">
            <h3>Guest Details</h3>
            <DataTable
                data={guests}
                columns={guestColumns}
                dense={false}
                noHeader
            />
        </div>
    );
};

const TicketTable = ({ tickets }) => {
    const [expandedRows, setExpandedRows] = useState([]);

    useEffect(() => {
        setExpandedRows(tickets.map((_, index) => index.toString()));
    }, [tickets]);

    const ticketColumns = [
        {
            name: "Ticket Type",
            selector: (row) => row.ticket_title,
            cell: row => (
                <div>
                    <div className="ticket-title">{row.ticket_title}</div>
                    <div className="ticket-category">{row.category_name}</div>
                </div>
            ),
            minWidth: "120px"
        },
        {
            name: "Status",
            selector: (row) => row.ticket_status,
            cell: row => (
                <span className={`status-badge ${row.ticket_status}`}>
                    {row.ticket_status}
                </span>
            ),
            minWidth: "120px"
        },
        {
            name: "Ticket Amount",
            selector: (row) => row.net_rate,
            cell: row => (
                <div className="ticket-amount">
                    {`${row?.net_rate} ${row?.currency_code}`}
                </div>
            ),
            minWidth: "120px"
        },
        {
            name: "TRP Amount",
            selector: (row) => row.net_rate_inr,
            cell: row => (
                <div className="trp-amount">
                    {`${row?.net_rate_inr} INR`}
                </div>
            ),
            minWidth: "120px"
        },
        {
            name: "Quantity Booked",
            selector: (row) => row.reserv_quantity,
            cell: row => <div className="quantity">{row.reserv_quantity}</div>,
            minWidth: "120px"
        },
    ];

    return (
        <div className="ticket-table-container">
            <DataTable
                data={tickets}
                columns={ticketColumns}
                dense={false}
                expandableRows
                expandableRowsComponent={({ data }) => (
                    <GuestTable guests={data.guests} />
                )}
                expandableRowExpanded={row => true}
                expandableRowsHideExpander
            />
        </div>
    );
};

const EventTable = ({ events }) => {
    const [expandedRows, setExpandedRows] = useState([]);

    useEffect(() => {
        setExpandedRows(events.map((_, index) => index.toString()));
    }, [events]);

    const eventColumns = [
        {
            name: "Event",
            selector: (row) => row.event_name,
            cell: row => (
                <div>
                    <div className="event-name">{row.event_name}</div>
                    <div className="tournament-name">{row.tournament_name}</div>
                </div>
            ),
            minWidth: "200px",
            grow: 2
        },
        {
            name: "Venue",
            selector: (row) => row.venue_name,
            cell: row => (
                <div>
                    <div className="venue-name">{row.venue_name}</div>
                    <div className="venue-city">{row.city}</div>
                </div>
            ),
            minWidth: "200px"
        },
        {
            name: "Event Start Date",
            selector: (row) => row.event_startdate,
            cell: row => (
                <div className="event-date">
                    {new Date(row.event_startdate).toLocaleString()}
                </div>
            ),
            minWidth: "180px"
        },
        {
            name: "Event Stop Date",
            selector: (row) => row.event_stopdate,
            cell: row => (
                <div className="event-date">
                    {new Date(row.event_stopdate).toLocaleString()}
                </div>
            ),
            minWidth: "180px"
        },
        {
            name: "Tournament",
            selector: (row) => row.tournament_name,
            cell: row => (
                <div>
                    <div className="tournament-name">{row.tournament_name}</div>
                    <div className="tournament-type">{row.tournament_type}</div>
                </div>
            ),
            minWidth: "200px",
            grow: 2
        },
        {
            name: "Event ID",
            selector: (row) => row.event_id,
            cell: row => (
                <div className="event-id">
                    {row.event_id}
                </div>
            ),
            minWidth: "180px"
        },
    ];

    return (
        <div className="event-table-container">
            <DataTable
                data={events}
                columns={eventColumns}
                dense={false}
                expandableRows
                expandableRowsComponent={({ data }) => (
                    <TicketTable tickets={data.tickets} />
                )}
                expandableRowExpanded={row => true}
                expandableRowsHideExpander
            />
        </div>
    );
};

const formatBookingData = (bookingData) => {
    if (!bookingData?.booking_ticket || !bookingData?.booking_guest) return [];
    const eventsMap = bookingData.booking_ticket.reduce((acc, ticket) => {
        const eventKey = ticket.event_id;

        if (!acc[eventKey]) {
            acc[eventKey] = {
                ...ticket,
                tickets: []
            };
        }

        const guests = bookingData.booking_guest.filter(
            guest => guest.trp_ticket_id === ticket.id
        );

        acc[eventKey].tickets.push({
            ...ticket,
            guests
        });

        return acc;
    }, {});

    return Object.values(eventsMap);
};

const BookingDetails = ({ bookingData }) => {
    const formattedData = formatBookingData(bookingData);

    return (
        <div className="booking-details-container">
            <div className="booking-header">
                <h1>Booking Details</h1>
            </div>
            <EventTable events={formattedData} />
        </div>
    );
};

export default BookingDetails;